import { useMemo, useState } from 'react';
import { useAppContext } from '@/context/AppContext';
import type { DebugStep, EventMeta } from '@/hooks/useDebugSession';
import {
  buildDaySummary,
  PERSONAL_PLACEHOLDER_ROW,
  type ActivityNode,
  type ClientNode,
  type SuggestionRow,
} from '@/lib/dayDiarySummary';
import { ScreenshotImage } from './ScreenshotImage';
import { ScreenshotLightbox, type ScreenshotLightboxEntry } from './ScreenshotLightbox';
import { formatDateTimeInTz } from '@/lib/dates';
import styles from './DayDiarySummaryPanel.module.css';

/** Pluralize "event" / "events" based on the count. */
function eventsLabel(n: number): string {
  return `${n.toLocaleString()} ${n === 1 ? 'event' : 'events'}`;
}

interface Props {
  readonly steps: readonly DebugStep[];
}

/**
 * Operator-facing extraction summary for the Day-Diary v2 step debugger.
 *
 * Walks every completed `consolidate.*` step in the session and renders a
 * client → project → activity tree, plus Internal, Personal, and
 * "newly proposed activities (no project)" buckets. Renders nothing when
 * no consolidate step has completed yet.
 *
 * Two expand levels (per spec):
 *   - **Activity level** — chevron next to each activity name; default
 *     open. Collapsing hides the suggestion list and its events.
 *   - **Suggestion level** — chevron on each suggestion row; default
 *     closed. Expanding shows the resolved events (app · title · url +
 *     screenshot thumbnail) the LLM clustered into that suggestion.
 *
 * Thumbnails reuse `<ScreenshotImage>` + `<ScreenshotLightbox>` so click
 * behavior matches the rest of the app. The lightbox set is global across
 * the panel — prev/next navigates across every expanded suggestion's
 * events at once.
 */
export function DayDiarySummaryPanel({ steps }: Props) {
  const summary = useMemo(() => buildDaySummary(steps), [steps]);
  const { mockupMode } = useAppContext();
  // The composite renders timestamps in UTC. CurrentOrg has no `timezone`
  // field in AppContext today (the org tz lives in `useOrgSettings`); the
  // debug page is operator-facing and uses UTC for the rest of the screen,
  // so consistency wins over local-time precision here.
  const tz = 'UTC';

  // Lightbox state — open + index into the full set of expanded events
  // that carry a screenshotId. Recomputing the set per render is cheap
  // (the tree is small, well under 100 events typical), and re-derives
  // automatically when the user expands/collapses suggestions because
  // expanded suggestions add their events to the visible list.
  const [lightboxEvent, setLightboxEvent] = useState<EventMeta | null>(null);
  const lightboxEntries = useMemo<ScreenshotLightboxEntry[]>(() => {
    const out: ScreenshotLightboxEntry[] = [];
    const visit = (events: readonly EventMeta[]) => {
      for (const e of events) {
        if (e.screenshotId !== null) {
          out.push({
            screenshotId: e.screenshotId,
            occurredAt: e.occurredAt,
            employeeName: null,
            altText: `${e.app}${e.title ? ` — ${e.title}` : ''}`,
          });
        }
      }
    };
    for (const c of summary.clients)
      for (const p of c.projects)
        for (const a of p.activities) for (const s of a.suggestions) visit(s.events);
    for (const s of summary.newActivitiesNoProject) visit(s.events);
    for (const s of summary.internal) visit(s.events);
    for (const a of summary.personal.existingActivities)
      for (const s of a.suggestions) visit(s.events);
    for (const s of summary.personal.placeholderRows) visit(s.events);
    return out;
  }, [summary]);

  const lightboxIndex =
    lightboxEvent === null
      ? -1
      : lightboxEntries.findIndex((e) => e.screenshotId === lightboxEvent.screenshotId);

  // Render nothing until the first consolidate has produced suggestions.
  if (summary.totals.suggestions === 0) return null;

  const openLightbox = (event: EventMeta) => {
    if (event.screenshotId !== null) setLightboxEvent(event);
  };
  const closeLightbox = () => setLightboxEvent(null);
  const prevLightbox = () => {
    if (lightboxIndex < 0 || lightboxEntries.length === 0) return;
    const next = (lightboxIndex - 1 + lightboxEntries.length) % lightboxEntries.length;
    const entry = lightboxEntries[next];
    if (entry) setLightboxEvent({ ...placeholderEvent, screenshotId: entry.screenshotId });
  };
  const nextLightbox = () => {
    if (lightboxIndex < 0 || lightboxEntries.length === 0) return;
    const next = (lightboxIndex + 1) % lightboxEntries.length;
    const entry = lightboxEntries[next];
    if (entry) setLightboxEvent({ ...placeholderEvent, screenshotId: entry.screenshotId });
  };

  return (
    <section className={styles.panel} aria-label="Extraction summary">
      <header className={styles.header}>
        <div className={styles.headerLabel}>Extraction summary</div>
        <div className={styles.headerStats}>
          <strong>{summary.totals.events.toLocaleString()}</strong> events ·{' '}
          <strong>{summary.totals.suggestions.toLocaleString()}</strong> suggestions ·{' '}
          <strong>{summary.totals.activities.toLocaleString()}</strong> activities ·{' '}
          <strong>{summary.totals.unattributable.toLocaleString()}</strong> unattributable
        </div>
      </header>

      {summary.clients.map((client) => (
        <ClientSection
          key={`client:${client.name}`}
          client={client}
          tz={tz}
          mockupMode={mockupMode}
          onOpenLightbox={openLightbox}
        />
      ))}

      {summary.newActivitiesNoProject.length > 0 && (
        <FlatBucket
          name="Newly proposed activities (no project)"
          totalEvents={sumEvents(summary.newActivitiesNoProject)}
          rows={summary.newActivitiesNoProject}
          tz={tz}
          mockupMode={mockupMode}
          onOpenLightbox={openLightbox}
        />
      )}

      <FlatBucket
        name="Internal"
        totalEvents={sumEvents(summary.internal)}
        rows={summary.internal}
        tz={tz}
        mockupMode={mockupMode}
        onOpenLightbox={openLightbox}
      />

      <PersonalSection
        totalEvents={summary.personal.totalEvents}
        existing={summary.personal.existingActivities}
        placeholderRows={summary.personal.placeholderRows}
        tz={tz}
        mockupMode={mockupMode}
        onOpenLightbox={openLightbox}
      />

      <ScreenshotLightbox
        open={lightboxEvent !== null && lightboxIndex >= 0}
        entries={lightboxEntries}
        index={Math.max(0, lightboxIndex)}
        tz={tz}
        onClose={closeLightbox}
        onPrev={prevLightbox}
        onNext={nextLightbox}
      />
    </section>
  );
}

// Stand-in event used when the lightbox advances by index alone — only the
// screenshotId field is read by the lightbox once an index resolves.
const placeholderEvent: EventMeta = {
  id: '',
  app: '',
  title: null,
  url: null,
  screenshotId: null,
  occurredAt: '',
  visionVerdict: null,
  categorization: null,
};

function ClientSection({
  client,
  tz,
  mockupMode,
  onOpenLightbox,
}: {
  client: ClientNode;
  tz: string;
  mockupMode: boolean;
  onOpenLightbox: (event: EventMeta) => void;
}) {
  return (
    <div className={styles.section}>
      <div className={styles.sectionTitleRow}>
        <span className={styles.sectionName}>{client.name}</span>
        <span className={styles.count}>{eventsLabel(client.totalEvents)}</span>
      </div>
      {client.projects.map((project) => (
        <div key={`project:${client.name}:${project.name}`} className={styles.projectGroup}>
          <div className={styles.projectTitleRow}>
            <span className={styles.projectName}>{project.name}</span>
            <span className={styles.count}>{eventsLabel(project.totalEvents)}</span>
          </div>
          {project.activities.map((activity) => (
            <ActivityGroup
              key={`activity:${client.name}:${project.name}:${activity.name}`}
              activity={activity}
              tz={tz}
              mockupMode={mockupMode}
              onOpenLightbox={onOpenLightbox}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

function ActivityGroup({
  activity,
  tz,
  mockupMode,
  onOpenLightbox,
}: {
  activity: ActivityNode;
  tz: string;
  mockupMode: boolean;
  onOpenLightbox: (event: EventMeta) => void;
}) {
  // Activity-level expand defaults to OPEN — the panel's primary job is
  // to show the suggestion breakdown. Collapsing folds away the noise.
  const [open, setOpen] = useState(true);
  return (
    <div className={styles.activityGroup}>
      <button
        type="button"
        className={styles.collapseTrigger}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span className={styles.chevron} aria-hidden="true">
          {open ? '▾' : '▸'}
        </span>
        <span className={styles.activityName}>
          {activity.isNew && <span className={styles.newBadge}>new</span>}
          {activity.name}
        </span>
        <span className={styles.count}>{eventsLabel(activity.totalEvents)}</span>
      </button>
      {open && (
        <SuggestionList
          rows={activity.suggestions}
          flat={false}
          tz={tz}
          mockupMode={mockupMode}
          onOpenLightbox={onOpenLightbox}
        />
      )}
    </div>
  );
}

function FlatBucket({
  name,
  totalEvents,
  rows,
  tz,
  mockupMode,
  onOpenLightbox,
}: {
  name: string;
  totalEvents: number;
  rows: readonly SuggestionRow[];
  tz: string;
  mockupMode: boolean;
  onOpenLightbox: (event: EventMeta) => void;
}) {
  return (
    <div className={styles.section}>
      <div className={styles.sectionTitleRow}>
        <span className={styles.sectionName}>{name}</span>
        <span className={styles.count}>{eventsLabel(totalEvents)}</span>
      </div>
      {rows.length === 0 ? (
        <div className={styles.empty}>(none)</div>
      ) : (
        <SuggestionList
          rows={rows}
          flat
          tz={tz}
          mockupMode={mockupMode}
          onOpenLightbox={onOpenLightbox}
        />
      )}
    </div>
  );
}

function PersonalSection({
  totalEvents,
  existing,
  placeholderRows,
  tz,
  mockupMode,
  onOpenLightbox,
}: {
  totalEvents: number;
  existing: readonly ActivityNode[];
  placeholderRows: readonly SuggestionRow[];
  tz: string;
  mockupMode: boolean;
  onOpenLightbox: (event: EventMeta) => void;
}) {
  const hasContent = existing.length > 0 || placeholderRows.length > 0;
  return (
    <div className={styles.section}>
      <div className={styles.sectionTitleRow}>
        <span className={styles.sectionName}>Personal</span>
        <span className={styles.count}>{eventsLabel(totalEvents)}</span>
      </div>
      {!hasContent && <div className={styles.empty}>(none)</div>}
      {existing.map((activity) => (
        <ActivityGroup
          key={`personal-activity:${activity.name}`}
          activity={activity}
          tz={tz}
          mockupMode={mockupMode}
          onOpenLightbox={onOpenLightbox}
        />
      ))}
      {placeholderRows.length > 0 && (
        <div className={styles.activityGroup}>
          <div className={styles.activityTitleRow}>
            <span className={styles.activityName}>{PERSONAL_PLACEHOLDER_ROW}</span>
            <span className={styles.count}>{eventsLabel(sumEvents(placeholderRows))}</span>
          </div>
          <SuggestionList
            rows={placeholderRows}
            flat={false}
            tz={tz}
            mockupMode={mockupMode}
            onOpenLightbox={onOpenLightbox}
          />
        </div>
      )}
    </div>
  );
}

function SuggestionList({
  rows,
  flat,
  tz,
  mockupMode,
  onOpenLightbox,
}: {
  rows: readonly SuggestionRow[];
  flat: boolean;
  tz: string;
  mockupMode: boolean;
  onOpenLightbox: (event: EventMeta) => void;
}) {
  return (
    <ul className={`${styles.suggestionList} ${flat ? styles.suggestionListFlat : ''}`}>
      {rows.map((row, i) => (
        <SuggestionItem
          key={`row:${i}:${row.title}`}
          row={row}
          tz={tz}
          mockupMode={mockupMode}
          onOpenLightbox={onOpenLightbox}
        />
      ))}
    </ul>
  );
}

function SuggestionItem({
  row,
  tz,
  mockupMode,
  onOpenLightbox,
}: {
  row: SuggestionRow;
  tz: string;
  mockupMode: boolean;
  onOpenLightbox: (event: EventMeta) => void;
}) {
  // Suggestion-level expand defaults to CLOSED — events are a drill-down,
  // not the default view. Disable expand when the suggestion carries no
  // resolved event metadata (backend didn't attach the events map yet).
  const [open, setOpen] = useState(false);
  const canExpand = row.events.length > 0;
  return (
    <li className={styles.suggestionRow}>
      <button
        type="button"
        className={styles.suggestionTrigger}
        onClick={() => canExpand && setOpen((v) => !v)}
        aria-expanded={canExpand ? open : undefined}
        disabled={!canExpand}
        title={canExpand ? undefined : 'No event detail attached for this suggestion'}
      >
        <span className={styles.chevron} aria-hidden="true">
          {!canExpand ? ' ' : open ? '▾' : '▸'}
        </span>
        <span className={styles.suggestionCount}>{row.eventCount}</span>
        <span className={styles.suggestionTitle}>
          {row.isNew && <span className={styles.newBadge}>new</span>}
          {row.title}
        </span>
      </button>
      {open && (
        <EventList
          events={row.events}
          tz={tz}
          mockupMode={mockupMode}
          onOpenLightbox={onOpenLightbox}
        />
      )}
    </li>
  );
}

function EventList({
  events,
  tz,
  mockupMode,
  onOpenLightbox,
}: {
  events: readonly EventMeta[];
  tz: string;
  mockupMode: boolean;
  onOpenLightbox: (event: EventMeta) => void;
}) {
  return (
    <ul className={styles.eventList}>
      {events.map((event) => (
        <EventRow
          key={`event:${event.id}`}
          event={event}
          tz={tz}
          mockupMode={mockupMode}
          onOpenLightbox={onOpenLightbox}
        />
      ))}
    </ul>
  );
}

/**
 * One event row. Renders thumbnail + metadata + (optional) vision pill +
 * (optional) categorization line. The vision verdict body is collapsible
 * behind the pill to keep the default density tight.
 */
function EventRow({
  event,
  tz,
  mockupMode,
  onOpenLightbox,
}: {
  event: EventMeta;
  tz: string;
  mockupMode: boolean;
  onOpenLightbox: (event: EventMeta) => void;
}) {
  const [visionOpen, setVisionOpen] = useState(false);
  return (
    <li className={styles.eventRow}>
      {event.screenshotId === null ? (
        <div className={styles.eventThumbEmpty} aria-hidden="true">
          <span className={styles.eventThumbEmptyLabel}>no screenshot</span>
        </div>
      ) : mockupMode ? (
        // Mockup placeholder: gray box labelled with the app name so the
        // visual hierarchy of the expanded view is readable without
        // contacting MinIO. The real ScreenshotImage path runs in live
        // mode.
        <div className={styles.eventThumbMock} aria-hidden="true">
          <span className={styles.eventThumbMockLabel}>{event.app}</span>
        </div>
      ) : (
        <ScreenshotImage
          screenshotId={event.screenshotId}
          width={120}
          height={80}
          onClick={() => onOpenLightbox(event)}
          clickAriaLabel={`Open screenshot for ${event.app}`}
          className={styles.eventThumb}
        />
      )}
      <div className={styles.eventMeta}>
        <div className={styles.eventTimeAppRow}>
          <span className={styles.eventTime}>{formatDateTimeInTz(event.occurredAt, tz)}</span>
          <span className={styles.eventApp}>{event.app}</span>
          {event.visionVerdict !== null && (
            <button
              type="button"
              className={`${styles.visionPill} ${
                styles[`visionPill_${event.visionVerdict.kind}`] ?? ''
              }`}
              onClick={() => setVisionOpen((v) => !v)}
              aria-expanded={visionOpen}
              title={`Stage 2 vision (${event.visionVerdict.kind}) — click to ${
                visionOpen ? 'hide' : 'show'
              } details`}
            >
              <span className={styles.visionPillIcon} aria-hidden="true">
                ◉
              </span>
              vision · {event.visionVerdict.kind} · {event.visionVerdict.verdict}
            </button>
          )}
        </div>
        {event.title !== null && event.title !== '' && (
          <div className={styles.eventTitle}>{event.title}</div>
        )}
        {event.url !== null && event.url !== '' && (
          <div className={styles.eventUrl} title={event.url}>
            {event.url}
          </div>
        )}
        {event.categorization !== null && (
          <div className={styles.eventCategorization}>
            <span className={styles.eventCategorizationLabel}>Currently:</span>{' '}
            {event.categorization.clientName !== null && (
              <>
                <span className={styles.eventCategorizationClient}>
                  {event.categorization.clientName}
                </span>
                {' → '}
              </>
            )}
            <span className={styles.eventCategorizationProject}>
              {event.categorization.projectName ?? 'Unclassified'}
            </span>{' '}
            <span className={styles.eventCategorizationSource}>
              (source: {event.categorization.source})
            </span>
          </div>
        )}
        {event.visionVerdict !== null && visionOpen && (
          <VisionVerdictPanel verdict={event.visionVerdict} />
        )}
      </div>
    </li>
  );
}

/**
 * Expanded vision-verdict body. Renders the kind-specific payload —
 * `refinedDescription` for enrich, `evidence` for attribute, `errorMessage`
 * for skipped — plus the shared `observed`, `observedText`, `reason`,
 * `confidence` fields. Compact, label/value style.
 */
function VisionVerdictPanel({ verdict }: { verdict: NonNullable<EventMeta['visionVerdict']> }) {
  return (
    <div className={styles.visionPanel}>
      <dl className={styles.visionPanelGrid}>
        <dt>verdict</dt>
        <dd>
          <span className={styles.visionVerdictValue}>{verdict.verdict}</span>
          {verdict.confidence !== null && (
            <span className={styles.visionConfidence}>
              {' '}
              · confidence {verdict.confidence.toFixed(2)}
            </span>
          )}
        </dd>
        {verdict.reason !== null && verdict.reason !== '' && (
          <>
            <dt>reason</dt>
            <dd>{verdict.reason}</dd>
          </>
        )}
        {verdict.observed !== null && verdict.observed !== '' && (
          <>
            <dt>observed</dt>
            <dd>{verdict.observed}</dd>
          </>
        )}
        {verdict.observedText.length > 0 && (
          <>
            <dt>observed text</dt>
            <dd>
              <ul className={styles.visionTokenList}>
                {verdict.observedText.map((tok, i) => (
                  <li key={`tok:${i}:${tok}`} className={styles.visionToken}>
                    {tok}
                  </li>
                ))}
              </ul>
            </dd>
          </>
        )}
        {verdict.refinedDescription !== null && verdict.refinedDescription !== '' && (
          <>
            <dt>refined description</dt>
            <dd>{verdict.refinedDescription}</dd>
          </>
        )}
        {verdict.evidence !== null && verdict.evidence !== '' && (
          <>
            <dt>evidence</dt>
            <dd>{verdict.evidence}</dd>
          </>
        )}
        {verdict.errorMessage !== null && verdict.errorMessage !== '' && (
          <>
            <dt>error</dt>
            <dd className={styles.visionError}>{verdict.errorMessage}</dd>
          </>
        )}
      </dl>
    </div>
  );
}

function sumEvents(rows: readonly SuggestionRow[]): number {
  let n = 0;
  for (const r of rows) n += r.eventCount;
  return n;
}
